import { useState } from "react";
import clsx from "clsx";
import { ArrowLeft, Plus } from "lucide-react";
import { AcDialog, AddRow, Button, Card, EmptyState, Field, FormRow, FormDialog, Icon, Page, Picker, ProBadge, Skeleton, Tag, DialogCancel } from "@cntrl/ui";
import { ApiError, errorMessage } from "../../../app/hooks/apiError.js";
import { pushToast } from "../../../app/toasts.js";
import { useBandContext } from "../../../app/bandContext.js";
import {
  useAdvanceLooks, useCreateAdvanceLook, useDeleteAdvanceLook, useVenueToken,
  type AdvanceLookView,
} from "../../../app/hooks/useProduction.js";
import { PUBLIC_WEB_URL } from "../../../config/env.js";
import { AdvanceDepartments } from "./AdvanceDepartments.js";
import { AdvanceLinkModal } from "./AdvanceLinkModal.js";
import { ADVANCE_KINDS, advanceTitle, linkChip, namePlaceholder, nameFieldLabel } from "./advanceFormat.js";
import styles from "./AdvanceCatalogue.module.scss";

/**
 * ARTIST CNTRL PRO — the advance CATALOGUE (ART-146, Arc 4 of `.design-specs/pro-tier.plan.md` §6).
 *
 * **Pro REPLACES the production surface; it never renders both.** That is the one section-level check, made
 * by `ProductionScreen`, and there is no per-widget tier test anywhere below this line.
 *
 * Underneath, a Pro "advance" is the SAME `artist.advance_looks` row a Standard look is — wearing a kind, a
 * year, a link password and a link expiry. So a band that lapses from Pro finds every advance still there as
 * a look on the classic pill row, with its files, notes, input list and venue link intact. Only the surface
 * goes, which is the rule this whole tier is built on.
 *
 * Two views, one component, because they share the list: the CATALOGUE (a row per advance) and RECALL (one
 * advance open, its twelve department tabs). `Regular` is not in either — it is the absence of a look, so a
 * Pro band reaches its club pack through the Standard editor exactly as before.
 */
export function AdvanceCatalogue({ canWrite }: { canWrite: boolean }) {
  const { shell } = useBandContext();
  const q = useAdvanceLooks();

  // MOBILE is CATALOGUE + LINK SETTINGS + READ-ONLY RECALL (spec §6.5). Nothing is born on a phone:
  // "desktop creates, mobile consumes", which is the handoff's own change request — the legacy shipped a
  // `.madv-add` create path and asked for it back. We get that by never building it.
  //
  // It is expressed as ONE derived flag rather than a second component, because a mobile fork of this screen
  // would be a second reading of the same rows and would drift from this one the first time either changed.
  // The SERVER half of the gate is the More row, which a Standard band never sees at all.
  const canAuthor = canWrite && shell !== "mobile";
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [linkFor, setLinkFor] = useState<AdvanceLookView | null>(null);

  const items = (q.data?.items ?? []).filter((l): l is AdvanceLookView & { lookId: string } =>
    typeof l.lookId === "string");
  const remaining = q.data?.remainingSlots ?? 0;
  const open = openId ? items.find((l) => l.lookId === openId) ?? null : null;

  // "Advance Docs" with the crimson Pro badge beside it — the header on every Pro view of this screen.
  const proTitle = <span className={styles.proTitle}>Advance Docs <ProBadge size={22} /></span>;

  // Loading / error, once, above both views.
  if (!q.data) {
    return (
      <Page title={proTitle}>
        <Card>
          {q.isError
            ? <EmptyState title="Couldn't load your advances"
                description={q.error instanceof ApiError ? q.error.message : "Check your connection and try again."}
                action={<Button onClick={() => void q.refetch()}>Retry</Button>} />
            : <div className={styles.skeletons} role="status" aria-live="polite" aria-label="Loading">
                <Skeleton variant="line" width="60%" /><Skeleton variant="line" width="80%" />
              </div>}
        </Card>
      </Page>
    );
  }

  const linkModal = linkFor && (
    <AdvanceLinkModal
      look={linkFor}
      onClose={() => setLinkFor(null)}
    />
  );

  // ---- RECALL: one advance, its departments -------------------------------------------------------------
  if (open) {
    const chip = linkChip(open);
    return (
      <Page>
        <div className={styles.recallBar}>
          <Button variant="ghost" size="sm" onClick={() => setOpenId(null)}>
            <Icon icon={ArrowLeft} size={16} /> All advances
          </Button>
          <span className={styles.recallName}>{advanceTitle(open)}</span>
          <span className={styles.recallRight}>
            {chip && <Tag tone={chip.tone} size="sm">{chip.label}</Tag>}
            {canWrite && (
              <Button variant="ghost" size="xs" onClick={() => setLinkFor(open)}>Link settings</Button>
            )}
            {/* Delete lives on the advance itself, beside Link settings — destroying the whole advance sat
                oddly among the link's url/password/expiry. Author-only: the recall view is read-only on a phone. */}
            {canAuthor && <DeleteAdvanceButton look={open} onDeleted={() => setOpenId(null)} />}
          </span>
        </div>
        <Card>
          <AdvanceDepartments lookId={open.lookId} canWrite={canAuthor} />
        </Card>
        {linkModal}
      </Page>
    );
  }

  // ---- CATALOGUE ----------------------------------------------------------------------------------------
  return (
    <Page title={proTitle}>
      <Card>
        {items.length === 0 ? (
          <EmptyState
            title="No advances yet"
            description={canAuthor
              ? undefined
              : "Advances are created on desktop — this is where you read and share them."}
            action={canAuthor ? <Button onClick={() => setCreating(true)}><Icon icon={Plus} size={16} /> New advance</Button> : undefined}
          />
        ) : (
          <ul className={styles.list}>
            {items.map((l) => {
              const chip = linkChip(l);
              return (
                <li key={l.lookId} className={styles.row}>
                  <button type="button" className={styles.rowName} onClick={() => setOpenId(l.lookId)}>
                    {advanceTitle(l)}
                  </button>
                  <span className={styles.rowChips}>
                    {chip
                      ? <Tag tone={chip.tone} size="sm">{chip.label}</Tag>
                      // "No link yet" is its OWN state, never folded into "open": telling a band its advance
                      // is public when nothing has been published is the worst reading available here.
                      : <Tag tone="muted" size="sm">No link yet</Tag>}
                  </span>
                  <span className={styles.rowActions}>
                    {/* "Copy link", not "Link" (ART-221): the button no longer opens Link settings — those live
                        on the recall bar (the editor), where the whole link's password/expiry/rotate sit. Here
                        it just copies the public url, so it is shown only once a link exists to copy. */}
                    {canWrite && l.hasVenueToken && <CopyLinkButton look={l} />}
                    <Button variant="ghost" size="xs" onClick={() => setOpenId(l.lookId)}>Edit content</Button>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        {/* "New advance" sits BELOW the list, styled like the standard editor's Add rows (dashed pill). Only
            when there is a list to sit below AND room to create; the empty state carries its own create CTA. */}
        {items.length > 0 && canAuthor && remaining > 0 && (
          <AddRow onClick={() => setCreating(true)}>New advance</AddRow>
        )}
        {canAuthor && remaining === 0 && items.length > 0 && (
          <p className={clsx(styles.hint, styles.capNote)}>
            You&rsquo;ve reached the advance limit. Delete one you no longer need to make room.
          </p>
        )}
      </Card>

      {creating && (
        <NewAdvanceModal
          existing={items}
          onClose={() => setCreating(false)}
          onCreated={(lookId) => { setCreating(false); setOpenId(lookId); }}
        />
      )}
      {linkModal}
    </Page>
  );
}

/**
 * NEW ADVANCE — type + name + year, and one of three seeds.
 *
 * The seeds are `blank` / `clone` / `legacy`, and there are exactly THREE: the CSV seed died in the
 * advance-docs pivot (2026-07-28, *"files and notes only"*) along with the Audio Bible, and must not come
 * back. `blank` is the default here because an arena advance has little to do with the club pack — but the
 * Standard create path sends no seed at all and still gets `legacy`, so nothing about that surface moved.
 */
function NewAdvanceModal(props: {
  existing: (AdvanceLookView & { lookId: string })[];
  onClose: () => void;
  onCreated: (lookId: string) => void;
}) {
  const create = useCreateAdvanceLook();
  const [kind, setKind] = useState<string>("tour");
  const [name, setName] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [seed, setSeed] = useState("blank");
  const [source, setSource] = useState("");
  const [error, setError] = useState<string | null>(null);

  const onSubmit = () => {
    if (!name.trim()) { setError("Give the advance a name — the tour, fest or event."); return; }
    if (seed === "clone" && !source) { setError("Pick which previous advance to copy from."); return; }
    setError(null);
    create.mutate(
      {
        name: name.trim(),
        kind,
        ...(year.trim() ? { year: Number(year) } : {}),
        seed,
        ...(seed === "clone" ? { sourceLookId: source } : {}),
      },
      {
        onSuccess: (look) => { if (look.lookId) props.onCreated(look.lookId); },
        onError: (e) => setError(errorMessage(e, "Couldn't create the advance.")),
      },
    );
  };

  return (
    <FormDialog
      title="New advance"
      onClose={props.onClose}
      size="md"
      bodyAs="form"
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
      footer={
        <>
          <DialogCancel />
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Creating…" : "Create advance"}
          </Button>
        </>
      }
    >
      {/* A real group, named once — the buttons are not labelable elements, so this is a `role=group` with
          its own label rather than a fieldset whose legend cannot be laid out by the flex gap. */}
      <div role="group" aria-label="Advance type" className={styles.kindRow}>
        {ADVANCE_KINDS.map((k) => (
          <button key={k.value} type="button" aria-pressed={kind === k.value}
            className={clsx(styles.kindBtn, kind === k.value && styles.kindBtnOn)}
            onClick={() => setKind(k.value)}>
            {k.label}
          </button>
        ))}
      </div>

      <FormRow>
        {/* The name's LABEL follows the type — a tour has a tour name — which is the only thing the segmented
            control changes about this form. */}
        <Field label={nameFieldLabel(kind)} value={name} placeholder={namePlaceholder(kind)}
          onChange={(e) => setName(e.target.value)} />
        <Field label="Year" value={year} inputMode="numeric" placeholder="2026"
          onChange={(e) => setYear(e.target.value.replace(/\D/g, "").slice(0, 4))} />
      </FormRow>

      <Picker
        label="Start from"
        value={seed}
        onChange={setSeed}
        options={[
          { value: "blank", label: "Blank advance" },
          // Offered only when there IS a previous advance — a choice whose only outcome is a second empty
          // picker is the dead affordance this codebase refuses elsewhere.
          //
          // The "Current docs (your Regular advance)" seed is deliberately NOT offered: Regular holds
          // Standard-shaped data (input list / file categories / rider) that the Pro department view can't
          // surface, so seeding from it brought nothing over. Managing a default advance is deferred.
          ...(props.existing.length > 0 ? [{ value: "clone", label: "A previous advance" }] : []),
        ]}
      />
      {seed === "clone" && (
        <Picker
          label="Copy from"
          value={source}
          onChange={setSource}
          placeholder="Pick an advance…"
          options={props.existing.map((l) => ({ value: l.lookId, label: advanceTitle(l) }))}
        />
      )}

      {error && <p className={styles.error} role="alert">{error}</p>}
    </FormDialog>
  );
}

/**
 * COPY LINK — the catalogue row's public-url copy (ART-221). It replaced the row's old "Link" button, which
 * opened Link settings; those now live only on the recall bar (the editor), because the row does not need to
 * carry the whole password/expiry/rotate surface — just the copy.
 *
 * The url is NOT on the read-gated looks list (the token is a bearer credential kept off `production:read`), so
 * this fetches it through the manage-gated venue-token route — which is exactly why the button is mounted only
 * under `canWrite`. Fetching on mount (not on click) keeps the clipboard write inside the user's gesture: an
 * `await` between the click and `writeText` loses user activation in Safari/Firefox. The row only renders this
 * when the advance already has a link, so `hasVenueToken` is true and `publicPath` will be present.
 */
function CopyLinkButton({ look }: { look: AdvanceLookView & { lookId: string } }) {
  const q = useVenueToken(look.lookId);
  const url = q.data?.publicPath ? `${PUBLIC_WEB_URL}${q.data.publicPath}` : null;

  const onCopy = async () => {
    if (!url) return;
    try { await navigator.clipboard.writeText(url); pushToast("Advance link copied.", "success"); }
    catch { pushToast("Couldn't copy the link.", "error"); }
  };

  return (
    <Button variant="ghost" size="xs" onClick={() => void onCopy()} disabled={!url}>
      Copy link
    </Button>
  );
}

/**
 * DELETE — one advance, from the recall bar beside Link settings. Confirmed through `AcDialog` like every
 * destructive op in this feature, and never a bare click: it kills a url venues are already holding and drops
 * the advance's files, notes and departments. On success the caller leaves the recall view (the row it was
 * reading is gone). Author-only — mounted only under `canAuthor`, so a phone never renders it.
 */
function DeleteAdvanceButton(props: {
  look: AdvanceLookView & { lookId: string };
  onDeleted: () => void;
}) {
  const remove = useDeleteAdvanceLook();
  const [confirm, setConfirm] = useState(false);

  const onDelete = () =>
    remove.mutate({ lookId: props.look.lookId }, {
      onSuccess: () => { setConfirm(false); props.onDeleted(); pushToast("Advance deleted.", "success"); },
      onError: (e) => pushToast(errorMessage(e, "Couldn't delete the advance."), "error"),
    });

  return (
    <>
      <Button variant="ghost" size="xs" tone="danger" onClick={() => setConfirm(true)}>
        Delete advance
      </Button>
      {confirm && (
        <AcDialog
          title={`Delete "${advanceTitle(props.look)}"?`}
          message="The public link dies instantly and the advance can't be recalled — its files, notes and departments go with it. Your Regular advance is untouched."
          confirmLabel="Delete advance"
          tone="danger"
          loading={remove.isPending}
          onCancel={() => setConfirm(false)}
          onConfirm={onDelete}
        />
      )}
    </>
  );
}
